import { MatchVideoAccessService } from '../../services/match-video-access.service'
import { UploadAreaAccessService } from '../../../upload-area/upload-area-access.service'
import { Component, ElementRef, HostListener, computed, effect, inject, signal, viewChild } from '@angular/core'
import { NavigationEnd, Router, RouterModule } from '@angular/router'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { filter } from 'rxjs'
import { AuthService } from '../../../auth/services/auth.service'
import { User, UserRole } from '../../interfaces/user.interface'
import { AvatarComponent } from '../avatar/avatar.component'
import { ContextIndicatorComponent } from '../context-indicator/context-indicator.component'
import { NavContextService, NavSeason } from '../../services/nav-context.service'
import { OverlayHistoryService, OverlayRelease } from '../../services/overlay-history.service'
import { ThemeService } from '../../services/theme.service'
import { ViewportService } from '../../services/viewport.service'
import { holdOverlayWhile } from '../../services/open-overlays.service'
import { environment } from '../../../../environments/environment'
import { LiveNavButtonComponent } from '../../../live/live-nav-button/live-nav-button.component'
import { AppLauncherComponent } from '../app-launcher/app-launcher.component'
import { AccountPanelComponent } from '../app-launcher/account-panel.component'
import { launcherSections } from '../app-launcher/launcher-items'
import { AcademyService } from '../../../academy/academy.service'
import { RefereeAccountsService } from '../../../referees/referee-accounts.service'
import {
  LucideCalendar, LucideChartColumn, LucideChevronLeft, LucideClipboardList, LucideClock,
  LucideGoal, LucideGrip, LucideLayoutDashboard, LucideTrophy, LucideUser, LucideUsers, LucideVideo, LucideWaypoints, LucideX, LucideZap,
} from '@lucide/angular'

type NavPanel = 'apps' | 'account'

import { NotificationBellComponent } from '../notification-bell/notification-bell.component'
import { WhatsAppHelpButtonComponent, injectWhatsAppHelpHref } from '../whatsapp-help-button/whatsapp-help-button.component'
import { WhatsNewSpotDirective } from '../../whats-new/whats-new-spot.directive'

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [WhatsNewSpotDirective, 
    RouterModule,
    LiveNavButtonComponent,
    AvatarComponent,
    ContextIndicatorComponent,
    AppLauncherComponent,
    AccountPanelComponent,
    NotificationBellComponent,
    WhatsAppHelpButtonComponent,
    LucideCalendar,
    LucideChartColumn,
    LucideChevronLeft,
    LucideClipboardList,
    LucideClock,
    LucideGrip,
    LucideGoal,
    LucideLayoutDashboard,
    LucideTrophy,
    LucideUser,
    LucideUsers,
    LucideVideo,
    LucideWaypoints,
    LucideX,
    LucideZap,
  ],
  templateUrl: './navbar.component.html',
  styles: [`
    /* The bar sits under the iPhone status bar / notch in the installed app (viewport-fit=cover):
       pad it by the safe areas, on the same surface colour. */
    :host { padding-top: env(safe-area-inset-top, 0px); background: var(--scios-surface-1); }
    .nav-row {
      padding-left: max(12px, env(safe-area-inset-left, 0px));
      padding-right: max(12px, env(safe-area-inset-right, 0px));
    }
    @media (min-width: 768px) {
      .nav-row {
        padding-left: max(20px, env(safe-area-inset-left, 0px));
        padding-right: max(20px, env(safe-area-inset-right, 0px));
      }
    }
    .nav-logo-icon { width: 30px; height: 28px; object-fit: contain; }
    /* Thumb-sized home link on phones: the 30px logo centred in a 44px target (Release 5,
       31/10/2026, SHELL-11). */
    @media (max-width: 767.98px) {
      .nav-home { min-width: 44px; min-height: 44px; justify-content: center; }
    }

    /* Tablet and desktop strip. "safe center": centred when it fits, start-aligned and scrolling
       when it does not (it used to overflow both edges at 768-1279px). The vertical padding is
       room for the active tab's lift and glow inside the scroll box. */
    .nav-tabs-wrap { justify-content: center; }
    .nav-tabs {
      display: flex;
      align-items: center;
      gap: 12px;
      min-width: 0;
      max-width: 100%;
      overflow-x: auto;
      padding: 6px 4px;
      margin: -6px 0;
      justify-content: safe center;
      scrollbar-width: none;
    }
    .nav-tabs::-webkit-scrollbar { display: none; }
    .nav-tab {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 0.65rem 1.2rem;
      border-radius: 8px;
      border: 1px solid var(--scios-border);
      background: transparent;
      font-weight: 600;
      color: var(--scios-text-secondary);
      text-decoration: none;
      white-space: nowrap;
      flex-shrink: 0;
      transition: all calc(150ms * var(--motion, 1)) cubic-bezier(0.4, 0, 0.2, 1);
    }
    @media (max-width: 1279.98px) {
      .nav-tabs { gap: 8px; }
      .nav-tab { padding: 0.55rem 0.85rem; gap: 6px; }
    }
    /* Only lift on devices with a real pointer — on touch the :hover state
       sticks after a tap and leaves the tab looking permanently active. */
    @media (hover: hover) {
      .nav-tab:hover {
        color: var(--scios-text-primary);
        background: var(--accent-primary-10);
        border-color: var(--accent-primary-25);
        transform: translateY(-2px);
      }
    }
    .nav-tab--active {
      color: var(--accent-primary);
      background: var(--accent-primary-15);
      border-color: var(--accent-primary);
      box-shadow: 0 0 16px var(--accent-primary-25), inset 0 0 8px var(--accent-primary-10);
      transform: translateY(-2px);
    }
    .nav-tab__icon { display: inline-flex; line-height: 1; }
    /* Seven or more tabs (a competition with Goal Analysis and Compare) do not fit a laptop at the
       full size: the last one hid behind Ask us at 1280px (Andreas, 02/11/2026). Tighter up to
       1535px, and the icons go below 1440px; the strip still scrolls if it must. */
    @media (max-width: 1535.98px) {
      .nav-tabs--many { gap: 6px; }
      .nav-tabs--many .nav-tab { padding: 0.5rem 0.75rem; gap: 6px; }
    }
    @media (max-width: 1439.98px) {
      .nav-tabs--many .nav-tab__icon { display: none; }
    }
    .nav-tab__label { font-size: 14px; }
    .nav-tab:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: 2px; }

    /* Phone tab row: under the bar, in the same sticky header. Hidden from tablet width up from
       this unlayered origin -- a Tailwind md:hidden would lose to the display below. */
    .ptabs {
      display: flex;
      align-items: stretch;
      height: 44px;
      background: var(--scios-surface-1);
      border-bottom: 1px solid var(--scios-border);
      padding-left: env(safe-area-inset-left, 0px);
      padding-right: env(safe-area-inset-right, 0px);
    }
    @media (min-width: 768px) {
      .ptabs { display: none; }
    }
    .ptabs__up {
      flex: 0 0 44px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      color: var(--accent-primary);
      border-right: 1px solid var(--scios-border-subtle);
    }
    .ptabs__up:active { background: var(--accent-primary-10); }
    .ptabs__scroll {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      overflow-x: auto;
      overscroll-behavior-x: contain;
      scrollbar-width: none;
      padding: 0 4px;
    }
    .ptabs__scroll::-webkit-scrollbar { display: none; }
    /* The fades say "there is more this way" -- shown only while there is. */
    .ptabs__scroll--fade-end { mask-image: linear-gradient(to right, #000 calc(100% - 28px), transparent); }
    .ptabs__scroll--fade-start { mask-image: linear-gradient(to left, #000 calc(100% - 28px), transparent); }
    .ptabs__scroll--fade-start.ptabs__scroll--fade-end {
      mask-image: linear-gradient(to right, transparent, #000 28px, #000 calc(100% - 28px), transparent);
    }
    .ptab {
      flex: 0 0 auto;
      display: inline-flex;
      align-items: center;
      padding: 0 14px;
      font-size: 14px;
      font-weight: 600;
      color: var(--scios-text-secondary);
      text-decoration: none;
      white-space: nowrap;
      border-bottom: 3px solid transparent;
      border-top: 3px solid transparent;
    }
    .ptab--active { color: var(--accent-primary); border-bottom-color: var(--accent-primary); }
    .ptab:active { background: var(--accent-primary-10); }
    .ptab:focus-visible, .ptabs__up:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: -2px; }
    /* Season picker at the end of the row: the same rounded rectangle as the app's other
       filters (CC, 04/10/2026). The 44px height is a transparent border that takes taps but
       paints nothing (the fill is clipped to the padding box, the edge is a shadow). */
    .ptabs__season {
      flex: 0 0 auto;
      align-self: center;
      height: 44px;
      margin-right: 8px;
      padding: 0 22px 0 10px;
      border: 0 solid transparent;
      border-top-width: 7px;
      border-bottom-width: 7px;
      border-radius: 8px;
      background-clip: padding-box;
      background-color: var(--scios-surface-2);
      background-image: url("data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='%239ca3af'%3E%3Cpath fill-rule='evenodd' d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z' clip-rule='evenodd'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 7px center;
      background-size: 14px;
      box-shadow: inset 0 0 0 1px var(--scios-border);
      color: var(--scios-text-primary);
      font: inherit;
      font-size: 13px;
      font-weight: 600;
      appearance: none;
      color-scheme: light dark;
      cursor: pointer;
    }
    .ptabs__season option { background: var(--scios-surface-1); color: var(--scios-text-primary); }
    .ptabs__season:focus-visible { outline: none; box-shadow: inset 0 0 0 1px var(--accent-primary); }

    .nav-icon-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      flex-shrink: 0;
      border: none;
      border-radius: 8px;
      background: transparent;
      color: var(--scios-text-primary);
      cursor: pointer;
    }
    @media (hover: hover) {
      .nav-icon-btn:hover { background: var(--scios-surface-2); }
    }

    /* The launcher (grid) and account (avatar) buttons, and the panel they open. */
    /* Two 40px circles on every screen, Google's size (CC, 05/10/2026: the name + chevron
       made the corner heavy). The row padding brings the phone tap target to ~44px. */
    .launcher-btn { width: 40px; height: 40px; border-radius: 50%; color: var(--scios-text-secondary); }
    .account-btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      padding: 0;
      border: none;
      border-radius: 50%;
      background: transparent;
      cursor: pointer;
      font: inherit;
    }
    /* Phones: 44px, the thumb target (Release 5, 31/10/2026, SHELL-11). Padding outside a button
       is not tappable, so the row padding never made up the difference. A sideways phone too
       (Release 7, 04/11/2026, SHELL2-05): the phone-layout rule of shared/styles/_breakpoints.scss. */
    @media (max-width: 767.98px), (pointer: coarse) and (max-height: 500px) {
      .launcher-btn, .account-btn { width: 44px; height: 44px; }
    }
    @media (hover: hover) {
      .launcher-btn:hover, .account-btn:hover { background: var(--scios-surface-2); }
    }
    .launcher-btn--open,
    .launcher-btn--open:hover,
    .account-btn--open,
    .account-btn--open:hover {
      background: var(--accent-primary-15);
      color: var(--accent-primary);
      box-shadow: inset 0 0 0 1px var(--accent-primary-50);
    }
    .launcher-btn:focus-visible, .account-btn:focus-visible { outline: 2px solid var(--accent-primary); outline-offset: 2px; }

    /* Desktop: a card under the bar, right-aligned, scrolling inside itself when the
       window is short. */
    .nav-panel {
      position: absolute;
      top: calc(100% + 8px);
      right: 12px;
      z-index: 1002;
      width: 344px;
      max-width: calc(100vw - 24px);
      max-height: calc(100dvh - 110px);
      display: flex;
      flex-direction: column;
      border-radius: 28px;
      border: 1px solid var(--scios-border);
      background: var(--scios-surface-1);
      box-shadow: 0 28px 70px rgba(0, 0, 0, 0.45), 0 0 0 1px var(--accent-primary-10);
      overflow: hidden;
      animation: panel-in 180ms ease-out;
      transform-origin: top right;
    }
    .nav-panel-body {
      overflow-y: auto;
      overscroll-behavior: contain;
      padding: 12px;
    }
    @keyframes panel-in {
      from { opacity: 0; transform: translateY(-6px) scale(0.98); }
      to   { opacity: 1; transform: none; }
    }

    /* Phone: a bottom sheet in thumb reach, clear of the iPhone home bar. */
    .nav-sheet-backdrop {
      position: fixed;
      inset: 0;
      z-index: 1001;
      background: rgba(2, 5, 12, 0.6);
      animation: sheet-fade 200ms ease-out;
    }
    .nav-panel--sheet {
      position: fixed;
      top: auto;
      left: 0;
      right: 0;
      bottom: 0;
      width: auto;
      max-width: none;
      max-height: 88dvh;
      border-radius: 28px 28px 0 0;
      border-width: 1px 0 0;
      animation: sheet-in 260ms cubic-bezier(0.2, 0.8, 0.2, 1);
      transition: transform 120ms ease-out;
    }
    .nav-panel--sheet .nav-panel-body {
      padding: 0 12px calc(16px + env(safe-area-inset-bottom));
    }
    /* The account card pins Sign out to the bottom edge itself (its footer carries the
       bottom and home-bar spacing), so Sign out never needs a scroll. */
    .nav-panel-body--account,
    .nav-panel--sheet .nav-panel-body--account { padding-bottom: 0; }
    .nav-sheet-grab { flex-shrink: 0; touch-action: none; }
    .nav-sheet-handle {
      display: block;
      width: 40px;
      height: 5px;
      margin: 10px auto 2px;
      border-radius: 3px;
      background: var(--scios-border);
    }
    .nav-sheet-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 10px 6px 20px;
    }
    .nav-sheet-head .nav-icon-btn { width: 44px; height: 44px; border-radius: 50%; color: var(--scios-text-secondary); }
    .nav-sheet-title { margin: 0; font-size: 17px; font-weight: 600; color: var(--scios-text-primary); }
    @keyframes sheet-in {
      from { transform: translateY(100%); }
      to   { transform: translateY(0); }
    }
    @keyframes sheet-fade {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @media (prefers-reduced-motion: reduce) {
      .nav-panel, .nav-panel--sheet, .nav-sheet-backdrop { animation: none; transition: none; }
    }
  `],
  host: { class: 'block sticky top-0 z-[1000]' },
})
export class NavbarComponent {
  private readonly authService = inject(AuthService)
  private readonly router = inject(Router)
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef)
  private readonly themeService = inject(ThemeService)
  private readonly overlays = inject(OverlayHistoryService)
  readonly navCtx = inject(NavContextService)
  readonly videoAccess = inject(MatchVideoAccessService)
  private readonly uploadAreaAccess = inject(UploadAreaAccessService)
  /** Whether this director has an Academy Overview (CC, 18/10/2026): adds its launcher tile. */
  private readonly academy = inject(AcademyService)
  private readonly refereeAccounts = inject(RefereeAccountsService)
  UserRole = UserRole

  currentUser = this.authService.currentUser

  protected readonly envBarText = environment.envBarText
  protected readonly theme = this.themeService.theme

  /** The phone layout: bottom sheets instead of cards. The app's one phone rule, so a sideways
   *  phone gets the sheet too (Release 7, 04/11/2026, SHELL2-05). */
  readonly isPhone = inject(ViewportService).isPhone
  /** Below 400px "Ask us on WhatsApp" moves into the account panel (Release 5, 31/10/2026). */
  readonly isNarrow = signal(window.innerWidth < 400)
  readonly whatsAppHref = injectWhatsAppHelpHref()

  /** Which top-right panel is open (CC, 04/10/2026): the launcher, the account card, or neither. */
  readonly panel = signal<NavPanel | null>(null)

  readonly launcherSections = computed(() => {
    const user = this.currentUser()
    return user ? launcherSections(user, this.videoAccess.canManage(), !!this.academy.landing()?.landing, this.authService.grantCompetitions().size > 0, this.authService.isScoutAgent(), this.authService.scouting(), this.refereeAccounts.observes(), this.authService.reachLoaded(), this.uploadAreaAccess.allowed(), this.refereeAccounts.observesOnly(), this.authService.grantNationalTeams().size > 0) : []
  })

  /** How far the phone sheet has been dragged down, in px (0 = resting). */
  readonly dragY = signal(0)
  private dragStart: number | null = null

  private readonly panelEl = viewChild<ElementRef<HTMLElement>>('panelEl')
  private readonly appsBtn = viewChild<ElementRef<HTMLButtonElement>>('appsBtn')
  private readonly accountBtn = viewChild<ElementRef<HTMLButtonElement>>('accountBtn')

  // An open panel owns a history entry, so the phone Back button closes it rather than
  // leaving the page underneath.
  private releasePanel: OverlayRelease | null = null

  togglePanel (which: NavPanel): void {
    const current = this.panel()
    if (current === which) {
      this.closePanel()
      return
    }
    // Switching straight from one panel to the other keeps the history entry it already has.
    if (!current) this.releasePanel = this.overlays.push(() => this.afterBack())
    this.panel.set(which)
    this.dragY.set(0)
    // Move focus into the panel, so keyboard and screen-reader users land in it.
    setTimeout(() => this.panelEl()?.nativeElement.querySelector<HTMLElement>('a[href], button')?.focus())
  }

  /** `navigating`: a panel link to another page, whose replaceUrl takes over the entry. */
  closePanel (navigating = false): void {
    const was = this.panel()
    if (!was) return
    this.panel.set(null)
    this.dragY.set(0)
    this.releasePanel?.({ navigating })
    this.releasePanel = null
    if (!navigating) this.returnFocus(was)
  }

  /** Closed by the Back button: its history entry is already gone. */
  private afterBack (): void {
    const was = this.panel()
    this.panel.set(null)
    this.dragY.set(0)
    this.releasePanel = null
    if (was) this.returnFocus(was)
  }

  private returnFocus (which: NavPanel): void {
    const btn = which === 'apps' ? this.appsBtn() : this.accountBtn()
    btn?.nativeElement.focus({ preventScroll: true })
  }

  // Drag down to close, on the phone sheet's grab area. Past 90px it closes; less springs back.
  onGrabStart (event: TouchEvent): void {
    this.dragStart = event.touches[0]?.clientY ?? null
  }

  onGrabMove (event: TouchEvent): void {
    if (this.dragStart === null) return
    const y = event.touches[0]?.clientY ?? this.dragStart
    this.dragY.set(Math.max(0, y - this.dragStart))
  }

  onGrabEnd (): void {
    if (this.dragStart === null) return
    this.dragStart = null
    if (this.dragY() > 90) this.closePanel()
    else this.dragY.set(0)
  }

  /** The nearest ancestor that links: the phone tab row's back chevron (as the breadcrumb chip). */
  readonly upCrumb = computed(() => {
    const parents = this.navCtx.breadcrumbs().slice(0, -1)
    return [...parents].reverse().find((i) => !!i.link) ?? null
  })

  private readonly phoneTabs = viewChild<ElementRef<HTMLElement>>('phoneTabs')
  readonly tabsFadeStart = signal(false)
  readonly tabsFadeEnd = signal(false)

  /** "2026/2027" -> "26/27": the phone tab row has room for the short form only. */
  shortSeason (season: string): string {
    return season.replace(/^20(\d\d)\/20(\d\d)$/, '$1/$2')
  }

  onSeasonChange (season: NavSeason, event: Event): void {
    const value = (event.target as HTMLSelectElement).value
    if (value) season.onChange(value)
  }

  /** Edge fades on the phone tab row, only while there is more to scroll to that way. */
  updateTabFades (): void {
    const el = this.phoneTabs()?.nativeElement
    if (!el) return
    this.tabsFadeStart.set(el.scrollLeft > 4)
    this.tabsFadeEnd.set(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)
  }

  /** Bring the current section's tab into view (Rankings and Stats start off-screen at 360px). */
  private scrollActiveTab (): void {
    // The phone row and, when it has to scroll (a small laptop), the laptop strip: the open tab
    // is never the one cut off (Andreas, 02/11/2026).
    const strip = this.host.nativeElement.querySelector<HTMLElement>('.nav-tabs')
    for (const [el, sel] of [[this.phoneTabs()?.nativeElement, '.ptab--active'], [strip, '.nav-tab--active']] as const) {
      const active = el?.querySelector<HTMLElement>(sel)
      if (el && active && el.scrollWidth > el.clientWidth) {
        const left = active.offsetLeft - (el.clientWidth - active.offsetWidth) / 2
        el.scrollTo({ left: Math.max(0, left), behavior: 'auto' })
      }
    }
    this.updateTabFades()
  }

  /* Desktop card: a click anywhere outside it and its two buttons closes it.
     The phone sheet has its own backdrop for that. */
  @HostListener('document:click', ['$event'])
  onDocumentClick (event: MouseEvent): void {
    if (!this.panel() || this.isPhone()) return
    // The path as it was when the click happened: a button that swaps the card's view (the
    // avatar opening Change photo, Back, Save) is already gone from the page by now, and
    // contains() would call it outside.
    const path = event.composedPath()
    const inside = [this.panelEl(), this.appsBtn(), this.accountBtn()]
      .some((ref) => !!ref && path.includes(ref.nativeElement))
    if (!inside) this.closePanel()
  }

  /* Two things can be open at once here, so Escape closes both rather than
     whichever handler happened to be registered last. */
  @HostListener('document:keydown.escape')
  onEscape (): void {
    this.closePanel()
  }

  /* An open panel stays open on resize/rotate and just switches between sheet and card. */
  @HostListener('window:resize')
  onResize (): void {
    this.isNarrow.set(window.innerWidth < 400)
    this.updateTabFades()
  }

  /* Links close their panel themselves, but browser back/forward and any other
     navigation would otherwise leave a panel hanging open over the new page. */
  constructor () {
    // The downloads bar steps aside while a panel is open (Release 7, 04/11/2026, SHELL2-01): this
    // bar's sticky z-index traps its sheets beneath the bar's z-index 1100.
    holdOverlayWhile(() => this.panel() !== null)
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), takeUntilDestroyed())
      .subscribe(() => { this.closePanel(true); setTimeout(() => this.scrollActiveTab()) })
    // New tabs (another entity, or a role-gated tab appearing): re-centre once they render.
    effect(() => {
      this.navCtx.tabs()
      setTimeout(() => this.scrollActiveTab())
    })
    // A director's launcher needs to know whether they have an academy, wherever they landed.
    effect(() => {
      const user = this.currentUser()
      if (user?.role === UserRole.DIRECTOR) void this.academy.loadLanding(user.id)
      // A referee's launcher shows My appointments only when they observe matches (R1.b).
      if (user?.role === UserRole.REFEREE) void this.refereeAccounts.loadObserves(user.id)
    })
  }

  getUserDisplayName (user: User): string {
    return `${user.first_name} ${user.last_name}`
  }

  getHomeLink (): string {
    return this.authService.getHomeRoute().join('/')
  }

  async logout (): Promise<void> {
    // The login page takes over the panel's history entry.
    this.closePanel(true)
    try {
      await this.authService.logout()
    } catch {
      // Navigate to login regardless of error
    }
    this.router.navigate(['/auth/login'], { replaceUrl: true })
  }
}
